/**
 * StageLadder — a journey progress rail whose ABSENT stages are rendered rather
 * than omitted. It plugs into `JourneyShell`'s `progress` slot alongside `Steps`
 * and `ProcessBar`; it does not replace either.
 *
 * ── why a third progress control, measured before it was written ───────────
 * `Steps` states are `done | active | upcoming`. `ProcessBar` states are
 * `done | active | upcoming`. Neither vocabulary can say **absent**, and the
 * two facts differ: `upcoming` promises a stage that WILL be reached, while
 * absent says the platform has no such environment and therefore knows nothing
 * about it. #1360 comment 42258 is exactly that distinction — a deployment with
 * no linked staging or production must show those stages as explicitly absent
 * rather than omit them, blank them, or report them as a zero. Rendering an
 * absent stage as `upcoming` would be a promise nothing has made.
 *
 * `ProcessBar` is also a focusable horizontally scrolling region by design
 * (its `label` prop exists for WCAG 2.1.1). This rail WRAPS instead — the F-30
 * responsive ruling forbids solving overflow by masking it, and a ladder of
 * four short tiles has no reason to scroll at 320px.
 *
 * ── the one rule this component exists to hold ─────────────────────────────
 * **Absence is not zero and an omission must be visible as an omission.** A
 * stage the deployment does not have is `state: 'absent'`: it keeps its place
 * in the ladder, renders its label, and carries a sentence saying what is not
 * known. `absence` is REQUIRED by the type on an absent stage — a
 * compiler-checked version of that rule, because the failure mode is silence
 * and silence does not fail a test nobody wrote.
 *
 * ── universality (the parity requirement, #1360 comment 42261) ─────────────
 * Nothing here knows any deployment, tenant, stage vocabulary or product name.
 * Labels, states, consequences and actions are DATA supplied by the caller from
 * what its own deployment reports. `StageLadder.test.tsx` proves it by
 * rendering a ladder belonging to nobody's reference deployment, and asserts
 * the source text holds no deployment proper noun at all.
 *
 * The words each STATE is announced with are data too (#2710): the host passes
 * `stateLabels` from its own localization, and the kit bakes no language. A
 * host that omits a word does not get a silent empty tile — the component
 * throws, naming the state, because an untyped (JSX) consumer is not stopped by
 * the required prop and silence does not fail a test nobody wrote.
 *
 * It fetches nothing and grants nothing: an `action` is a node the host passes
 * only after its governed door has said the act is available.
 */
import { useId, type ReactNode } from 'react'
import s from './StageLadder.module.css'

export type StageLadderState = 'done' | 'current' | 'pending' | 'absent'

interface StageBase {
  key: string
  /** The stage's own name, from the caller's data. Never derived here. */
  label: ReactNode
  /** What is true at this stage. */
  detail?: ReactNode
  /** What acting here — or leaving it as it is — would do. */
  consequence?: ReactNode
  /** The task-action this stage offers, already governed by the host. */
  action?: ReactNode
}

export type LadderStage =
  | (StageBase & {
      state: 'done' | 'current' | 'pending'
      absence?: never
    })
  /** An absent stage MUST say what is not known. Absence is not zero. */
  | (StageBase & { state: 'absent'; absence: ReactNode })

export interface StageLadderProps {
  /** Names the ladder for assistive technology and for the visible heading. */
  title: ReactNode
  headingLevel?: 2 | 3 | 4
  summary?: ReactNode
  stages: LadderStage[]
  /**
   * The word each state is announced with — not a colour, and not an icon
   * alone. REQUIRED, and supplied by the host from its own localization: the
   * kit carries no language, so a Dutch or French host announces its own words
   * rather than inheriting English ones. Every state a rendered stage uses must
   * have a non-empty word, or the component throws (see `stateWord`).
   */
  stateLabels: Readonly<Record<StageLadderState, string>>
  /** Rendered under the rail — the detail the ladder introduces. */
  children?: ReactNode
  testId?: string
}

/**
 * The host's word for one state, or a named failure. A plain check on purpose:
 * an untyped consumer that omits one key of `stateLabels` would otherwise
 * render an empty state tile — a stage announced by colour alone.
 */
function stateWord(stateLabels: StageLadderProps['stateLabels'], state: StageLadderState): string {
  const word: unknown = stateLabels[state]
  if (typeof word !== 'string' || word.trim().length === 0) {
    throw new Error(`StageLadder: stateLabels has no word for state '${state}'; the host supplies every state word (#2710)`)
  }
  return word
}

export function StageLadder({
  title, headingLevel = 2, summary, stages, stateLabels, children, testId,
}: StageLadderProps) {
  const generated = useId()
  // Before anything renders: the type requires the prop, but an untyped JSX
  // consumer is not checked by it, and a missing map must not degrade quietly.
  if (stateLabels == null) {
    throw new Error('StageLadder: no stateLabels were supplied; the host supplies every state word (#2710)')
  }
  const titleId = `ladder-${generated.replace(/:/g, '')}`
  const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4'

  return (
    <section className={s.ladder} data-testid={testId} aria-labelledby={titleId}>
      <Heading className={s.title} id={titleId}>{title}</Heading>
      {summary != null && <p className={s.detail}>{summary}</p>}
      <ol className={s.rail} data-testid={testId ? `${testId}-rail` : undefined}>
        {stages.map((stage) => (
          <li
            key={stage.key}
            className={s.stage}
            data-state={stage.state}
            data-stage={stage.key}
            data-testid="ladder-stage"
          >
            <div className={s.stageHead}>
              <h3 className={s.stageLabel}>{stage.label}</h3>
              <span className={s.state} data-testid="ladder-state">
                {stateWord(stateLabels, stage.state)}
              </span>
            </div>
            {stage.detail != null && <p className={s.detail}>{stage.detail}</p>}
            {stage.state === 'absent' && (
              <p className={s.absence} data-testid="ladder-absence">{stage.absence}</p>
            )}
            {stage.consequence != null && (
              <p className={s.consequence} data-testid="ladder-consequence">{stage.consequence}</p>
            )}
            {stage.action != null && <div className={s.action}>{stage.action}</div>}
          </li>
        ))}
      </ol>
      {children}
    </section>
  )
}
